import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useSWRConfig } from 'swr'

import ZeroTotalSendModal from '../../components/invoices/zeroTotalSendModal'
import { BusinessDetailsDraft } from '../../components/business/modal'
import config from '../../config'
import { useAuth } from '../../context/auth'
import { useAlerts } from '../../context/alerts'
import { executeProfileOverwriteAccept } from '../../lib/executeProfileOverwriteAccept'
import { guestDraftToBusinessDetailsDraft } from '../../lib/guestDraftToBusinessDetailsDraft'
import {
  buildSendEmailPath,
  EditPagePostHydrationOutcome,
  planEditPagePostHydration,
  planReadyPromptAction,
  planZeroTotalSendConfirmed,
  ReadyPromptUserAction,
} from '../../lib/guestEditPagePostHydrationOrchestrator'
import {
  ProfileOverwriteChoice,
  buildInvoicePdfUrl,
} from '../../lib/guestHydrationOrchestrator'
import {
  clearGuestInvoiceDraftStorage,
  clearGuestInvoiceReadyPromptSignal,
  loadGuestInvoiceDraft,
  markGuestProfileOverwriteResolved,
  readGuestInvoiceReadyPromptSignal,
} from '../../lib/guestInvoiceDraftStorage'
import { revalidateClientCaches } from '../../lib/revalidateClientCaches'
import ProfileOverwritePrompt from '../landing/ProfileOverwritePrompt'
import GuestInvoiceReadyPrompt from './GuestInvoiceReadyPrompt'

type Props = {
  invoiceId: string
  onBusinessDetailsApply: (request: { nonce: number; draft: BusinessDetailsDraft }) => void
}

const EditPagePostHydrationModals: React.FC<Props> = ({
  invoiceId,
  onBusinessDetailsApply,
}) => {
  const { user, mutate: mutateUser } = useAuth()
  const { addAlert } = useAlerts()
  const navigate = useNavigate()
  const { mutate: mutateCache } = useSWRConfig()

  const [guestDraft, setGuestDraft] = React.useState(() => loadGuestInvoiceDraft())
  const [sessionReadyPrompt] = React.useState(() => {
    const signal = readGuestInvoiceReadyPromptSignal()
    if (!signal || signal.invoiceId !== invoiceId) return null
    return signal
  })
  const [profileOverwriteResolved, setProfileOverwriteResolved] = React.useState(
    () => sessionReadyPrompt?.profileOverwriteResolved === true,
  )
  const [readyPromptDismissed, setReadyPromptDismissed] = React.useState(false)
  const [isAcceptingProfile, setIsAcceptingProfile] = React.useState(false)
  const [zeroTotalSendPath, setZeroTotalSendPath] = React.useState<string | null>(null)
  const businessApplyNonceRef = React.useRef(0)

  const savedProfile = React.useMemo(
    () => ({ businessDetails: user?.businessDetails }),
    [user?.businessDetails],
  )

  const plannerOutcome = React.useMemo(
    () => planEditPagePostHydration({
      guestDraft,
      savedProfile,
      sessionReadyPrompt,
      profileOverwriteResolved,
      readyPromptDismissed,
    }),
    [guestDraft, savedProfile, sessionReadyPrompt, profileOverwriteResolved, readyPromptDismissed],
  )

  const applyPlannerDraftClear = React.useCallback((outcome: EditPagePostHydrationOutcome) => {
    if (outcome.modal === 'none' && outcome.clearGuestDraft) {
      clearGuestInvoiceDraftStorage()
      setGuestDraft(null)
    }
  }, [])

  React.useEffect(() => {
    applyPlannerDraftClear(plannerOutcome)
  }, [applyPlannerDraftClear, plannerOutcome])

  const finishReadyPrompt = React.useCallback((outcome: EditPagePostHydrationOutcome) => {
    clearGuestInvoiceReadyPromptSignal()
    setReadyPromptDismissed(true)
    applyPlannerDraftClear(outcome)
  }, [applyPlannerDraftClear])

  const handleProfileChoice = async (choice: ProfileOverwriteChoice) => {
    if (choice === 'accept' && guestDraft) {
      setIsAcceptingProfile(true)
      const result = await executeProfileOverwriteAccept(guestDraft, savedProfile, user?.email)
      if (!result.ok) {
        addAlert({ type: 'error', message: 'Could not update your business profile. Please try again.' })
        setIsAcceptingProfile(false)
        return
      }

      await mutateUser()
      await revalidateClientCaches(mutateCache)
      businessApplyNonceRef.current += 1
      onBusinessDetailsApply({
        nonce: businessApplyNonceRef.current,
        draft: guestDraftToBusinessDetailsDraft(guestDraft, savedProfile, user?.email),
      })
      setIsAcceptingProfile(false)
    }

    markGuestProfileOverwriteResolved()
    setProfileOverwriteResolved(true)
  }

  const handleReadyPromptAction = (action: ReadyPromptUserAction) => {
    const outcome = planReadyPromptAction({ guestDraft, invoiceId, action })

    if (outcome.modal === 'zeroTotalSend') {
      setZeroTotalSendPath(outcome.sendEmailPath)
      return
    }

    finishReadyPrompt(outcome)

    if (action === 'send') {
      navigate(buildSendEmailPath(invoiceId))
      return
    }

    if (action === 'viewPdf') {
      window.open(buildInvoicePdfUrl(config.REACT_APP_API_URL, invoiceId), '_blank')
    }
  }

  const handleZeroTotalConfirm = () => {
    const outcome = planZeroTotalSendConfirmed(invoiceId)
    finishReadyPrompt(outcome)
    setZeroTotalSendPath(null)
    if (zeroTotalSendPath) {
      navigate(zeroTotalSendPath)
    }
  }

  const handleZeroTotalClose = () => {
    setZeroTotalSendPath(null)
  }

  if (plannerOutcome.modal === 'profileOverwrite') {
    return (
      <ProfileOverwritePrompt
        open
        disableDismiss={isAcceptingProfile}
        onChoose={(choice) => {
          if (isAcceptingProfile) return
          void handleProfileChoice(choice)
        }}
      />
    )
  }

  if (plannerOutcome.modal === 'guestInvoiceReady') {
    return (
      <>
        <GuestInvoiceReadyPrompt
          open
          pendingIntent={plannerOutcome.pendingIntent}
          primaryAction={plannerOutcome.primaryAction}
          onKeepEditing={() => handleReadyPromptAction('keepEditing')}
          onViewPdf={() => handleReadyPromptAction('viewPdf')}
          onSendInvoice={() => handleReadyPromptAction('send')}
        />
        <ZeroTotalSendModal
          open={zeroTotalSendPath != null}
          onClose={handleZeroTotalClose}
          onConfirm={handleZeroTotalConfirm}
        />
      </>
    )
  }

  return (
    <ZeroTotalSendModal
      open={zeroTotalSendPath != null}
      onClose={handleZeroTotalClose}
      onConfirm={handleZeroTotalConfirm}
    />
  )
}

export default EditPagePostHydrationModals
